import React from 'react';
import {TapHandlerLogic} from '../../tap-handler/tap-handler.component';
import {OrbitComponentWithState} from "../../base-components/orbit-component-with-state/orbit-component-with-state";

enum IMAGE_TYPE {
    EMPTY_IMAGE_BASE64,
    FALLBACK,
    SRC
};
enum IMAGE_STATUS {
    LAZY,
    LOADING,
    SETTLED
}

interface ImageSettleStatus {
    value: "load" | "error",
    type: IMAGE_TYPE,
}

/**
 * We could have defined this interface as "ImageProps extends React.ImgHTMLAttributes<HTMLImageElement>"
 * and define inside only what's extra (like "lazy" or "fallbackSrc") and props that alter the original def (like "width: number" instead of default "width: string | number")
 * But in case of someone styling the "Image" component with: styled(Image)<{squareIcon: boolean}>. The extra prop will bleed into the DOM and React will throw:
 * -- "Warning: React does not recognize the `squareIcon` prop on a DOM element. If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `squareicon` instead. If you accidentally passed it from a parent component, remove it from the DOM element."
 * Also, ocasionally I am getting this warning
 * -- "Warning: React does not recognize the `forwardedRef` prop on a DOM element. If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `forwardedref` instead. If you accidentally passed it from a parent component, remove it from the DOM element."
 *
 * So I am whitelisting props we actually use, and I am filtering based on that
 */
export interface ImageProps {
    // props common with React.ImgHTMLAttributes<HTMLImageElement>
    src: string;
    alt?: string;
    className?: string;
    title?: string;
    onClick?(event: React.MouseEvent<HTMLImageElement, MouseEvent>): void;
    onLoad?(e: React.SyntheticEvent<HTMLImageElement, Event>): void;
    onError?(e: React.SyntheticEvent<HTMLImageElement, Event>): void;

    // props derived from React.ImgHTMLAttributes<HTMLImageElement> with slight changes
    width?: number; // instead of string | number
    height?: number; // instead of string | number

    // custom props
    fallbackSrc?: string;
    enableTap?: boolean;
    lazy?: boolean;
    onLoadStart?(): void;
    onSettled?(settleStatus: ImageSettleStatus): void;
}

interface ImageState {
    src: string;
    type: IMAGE_TYPE;
    status: IMAGE_STATUS;
}

const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';

export class Image extends OrbitComponentWithState<ImageProps, ImageState> {

    constructor(props: ImageProps) {
        super(props);
        this.state = this._getInitialSrcState();
    }

    private _getInitialSrcState() {
        /**
         * If this is a lazy load image we initialize the state.src with the fallback image from props
         * but in case this is not provided we just set an empty image gif.
         * This will prevent the borders to be visible around the image while scrolled into the view.
         */
        if (this.props.lazy) {
            return {
                src: this.props.fallbackSrc || EMPTY_IMAGE,
                type: this.props.fallbackSrc ? IMAGE_TYPE.FALLBACK : IMAGE_TYPE.EMPTY_IMAGE_BASE64,
                status: IMAGE_STATUS.LAZY
            };
        }
        if (this.props.onLoadStart) {
            this.props.onLoadStart();
        }
        return {
            src: this.props.src,
            type: IMAGE_TYPE.SRC,
            status: IMAGE_STATUS.LOADING
        };
    }

    private _onLoad = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        if (this.state.status === IMAGE_STATUS.LAZY) {
            return;
        }
        if (this.state.status === IMAGE_STATUS.LOADING) {
            this.setState({
                status: IMAGE_STATUS.SETTLED
            });
            if (this.state.type === IMAGE_TYPE.SRC && this.props.onLoad) {
                this.props.onLoad(e);
            }
            if (this.props.onSettled) {
                this.props.onSettled({
                    value: "load",
                    type: this.state.type
                });
            }
        }
    }
    private _onError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        if (this.state.status === IMAGE_STATUS.LAZY && this.state.type === IMAGE_TYPE.FALLBACK) {
            this.setState({
                src: EMPTY_IMAGE,
                type: IMAGE_TYPE.EMPTY_IMAGE_BASE64,
            });
        }
        if (this.state.status === IMAGE_STATUS.LOADING) {
            if (this.state.type === IMAGE_TYPE.SRC) {
                this.setState({
                    src: this.props.fallbackSrc || EMPTY_IMAGE,
                    type: this.props.fallbackSrc ? IMAGE_TYPE.FALLBACK : IMAGE_TYPE.EMPTY_IMAGE_BASE64
                });
                if (this.props.onError) {
                    this.props.onError(e);
                }
            } else if (this.state.type === IMAGE_TYPE.FALLBACK) {
                this.setState({
                    src: EMPTY_IMAGE,
                    type: IMAGE_TYPE.EMPTY_IMAGE_BASE64,
                });
            } else if (this.state.type === IMAGE_TYPE.EMPTY_IMAGE_BASE64) {
                this.setState({
                    status: IMAGE_STATUS.SETTLED
                });
                if (this.props.onSettled) {
                    this.props.onSettled({
                        value: "error",
                        type: this.state.type
                    });
                }
            }
        }
    }

    componentWillUnmount() {
        super.componentWillUnmount();
        this._destroyIntersectionObserver();
        this._destroyTapLogicHandler();
    }

    componentDidUpdate(prevProps: ImageProps) {
        if (this.props.fallbackSrc !== prevProps.fallbackSrc && this.state.type === IMAGE_TYPE.FALLBACK) {
            this.setState({
                src: this.props.fallbackSrc
            });
        }
        if (this.props.src !== prevProps.src) {
            if (this.state.status === IMAGE_STATUS.SETTLED) {
                this.setState({
                    src: this.props.src,
                    type: IMAGE_TYPE.SRC
                });
            }
        }
    }

    private _tapHandlerLogic: TapHandlerLogic | null = null;
    private _createTapLogicHandler(element: HTMLElement) {
        if (this.props.enableTap) {
            this._destroyTapLogicHandler();
            this._tapHandlerLogic = new TapHandlerLogic(element);
        }
    }

    private _destroyTapLogicHandler() {
        if (this._tapHandlerLogic) {
            this._tapHandlerLogic.dispose();
            this._tapHandlerLogic = null;
        }
    }

    private _intersectionObserver: IntersectionObserver | null;

    private _createIntersectionObserver(element: HTMLImageElement) {
        if (!this.props.lazy) {
            return;
        }

        // just in case this is called twice for this component we want to discard the existing intersection observer and create a new one
        this._destroyIntersectionObserver();

        this._intersectionObserver = new IntersectionObserver(this._intersectionObserverCallback, {
            // if we don't attach to the correct scroll container (the one that has the scroll bars) then the rootMargin doesn't work correctly
            root: this.services.mainScreen.mainScreenElement,
            rootMargin: '15px',
            threshold: 0.1
        });

        this._intersectionObserver.observe(element);
    }

    private _destroyIntersectionObserver() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
            this._intersectionObserver = null;
        }
    }

    private _intersectionObserverCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
        if (entries[0].intersectionRatio > 0 || entries[0].isIntersecting) {
            this._destroyIntersectionObserver();

            this.setState({
                src: this.props.src,
                type: IMAGE_TYPE.SRC,
                status: IMAGE_STATUS.LOADING
            });

            if (this.props.onLoadStart) {
                this.props.onLoadStart();
            }
        }
    };

    private _captureImageElementRef = (element: HTMLImageElement) => {
        if (element) {
            this._createTapLogicHandler(element);
            this._createIntersectionObserver(element);
        }
    };

    render() {
        let {
            // standard img props that need to be altered
            alt,
            // pass-through props
            className, title, width, height,
        } = this.props;

        return <img
            className={className}
            title={title}
            width={width}
            height={height}
            /*alt is set here to avoid a react warning which says that all images must have an alt property*/
            alt={alt || ''}
            src={this.state.src}
            ref={this._captureImageElementRef}
            onLoad={this._onLoad}
            onError={this._onError}
            onClick={this.props.onClick}
        />;
    }

}
